import { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../Typography';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface StatItem {
  label: string;
  homeValue: number | string;
  awayValue: number | string;
}

export interface FixtureGroupProps {
  version?: 'match stats' | 'season stats';
  title?: string;
  stats: StatItem[];
  className?: string;
}

// ─── Sub-component: BarGraph (match stats) ────────────────────────────────────

interface BarGraphProps {
  label: string;
  homeValue: number | string;
  awayValue: number | string;
}

function computeFlexValues(
  home: number | string,
  away: number | string
): { homeFlex: number; awayFlex: number } {
  const h = parseFloat(String(home));
  const a = parseFloat(String(away));
  if (isNaN(h) || isNaN(a) || h + a === 0) return { homeFlex: 1, awayFlex: 1 };
  return { homeFlex: h, awayFlex: a };
}

const BarGraph: FC<BarGraphProps> = ({ label, homeValue, awayValue }) => {
  const { homeFlex, awayFlex } = computeFlexValues(homeValue, awayValue);

  return (
    <div className="flex flex-col gap-[--padding-xs] items-center justify-center pb-[--padding-md] pt-[--padding-sm] w-full bg-[--stats-bg]">
      {/* Stat labels row */}
      <div className="flex gap-[--padding-xs] items-center w-full whitespace-nowrap">
        {/* Mobile: subheadline020 (14px bold). Desktop: 16px bold (subheadline030). */}
        <Typography
          as="p"
          variant="subheadline020"
          className="w-[40px] shrink-0 text-[--stats-text] overflow-hidden text-ellipsis md:text-16 md:leading-20"
        >
          {homeValue}
        </Typography>
        {/* Mobile: paragraph010 (12px). Desktop: paragraph020 (14px). */}
        <Typography
          as="p"
          variant="paragraph010"
          className="flex-1 min-w-px text-center text-[--stats-text] overflow-hidden text-ellipsis md:text-14 md:leading-20"
        >
          {label}
        </Typography>
        <Typography
          as="p"
          variant="subheadline020"
          className="w-[40px] shrink-0 text-right text-[--stats-text] overflow-hidden text-ellipsis md:text-16 md:leading-20"
        >
          {awayValue}
        </Typography>
      </div>

      {/* Progress bars — flex-based proportional widths */}
      <div className="flex gap-[--padding-2xs] items-start w-full">
        <div
          className="h-[4px] rounded-[--corner-full] bg-[--stats-text-blue]"
          style={{ flex: homeFlex }}
        />
        <div
          className="h-[4px] rounded-[--corner-full] bg-[--stats-text-negative]"
          style={{ flex: awayFlex }}
        />
      </div>
    </div>
  );
};

// ─── Sub-component: PieGraph (season stats) ───────────────────────────────────

interface PieGraphProps {
  label: string;
  homeValue: number | string;
  awayValue: number | string;
}

/**
 * SVG donut chart. Blue arc = home, red arc = away.
 * Both arcs start at 12 o'clock (SVG rotated -90°) and go clockwise.
 * The larger value is shown first (bigger text) inside the circle; home is
 * always blue, away is always red.
 */
const PieChart: FC<{ home: number; away: number; size?: number }> = ({
  home,
  away,
  size = 106,
}) => {
  const cx = size / 2;
  const cy = size / 2;
  const strokeWidth = 12;
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const total = home + away;

  // Small visual gap (px) between the two arc caps.
  const gap = total === 0 ? 0 : 6;
  const homeDash =
    total === 0 ? circumference / 2 : Math.max(0, (home / total) * circumference - gap);
  const awayDash =
    total === 0 ? circumference / 2 : Math.max(0, (away / total) * circumference - gap);

  // Negative dashoffset shifts the arc start clockwise past the home arc + gap.
  const awayOffset = -(homeDash + gap);

  const homeIsPrimary = home >= away;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {/* Donut arcs — rotated so the start is at 12 o'clock */}
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        {/* Home arc (blue) */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--stats-text-blue)"
          strokeWidth={strokeWidth}
          strokeDasharray={`${homeDash} ${circumference}`}
          strokeLinecap="round"
        />
        {/* Away arc (red) — offset to start immediately after the home arc */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--stats-text-negative)"
          strokeWidth={strokeWidth}
          strokeDasharray={`${awayDash} ${circumference}`}
          strokeDashoffset={awayOffset}
          strokeLinecap="round"
        />
      </svg>

      {/* Centered value labels */}
      <div className="absolute inset-0 flex flex-col gap-0 items-center justify-center">
        <Typography
          as="p"
          variant="subheadline060"
          className={homeIsPrimary ? 'text-[--stats-text-blue]' : 'text-[--stats-text-negative]'}
        >
          {homeIsPrimary ? home : away}
        </Typography>
        <Typography
          as="p"
          variant="subheadline020"
          className={homeIsPrimary ? 'text-[--stats-text-negative]' : 'text-[--stats-text-blue]'}
        >
          {homeIsPrimary ? away : home}
        </Typography>
      </div>
    </div>
  );
};

const PieGraph: FC<PieGraphProps> = ({ label, homeValue, awayValue }) => {
  const h = parseFloat(String(homeValue));
  const a = parseFloat(String(awayValue));

  return (
    <div className="flex flex-col gap-[--padding-sm] items-center">
      <Typography
        as="p"
        variant="paragraph010"
        className="text-[--stats-text] text-center whitespace-nowrap"
      >
        {label}
      </Typography>
      <PieChart home={isNaN(h) ? 0 : h} away={isNaN(a) ? 0 : a} />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const FixtureGroup: FC<FixtureGroupProps> = ({
  version = 'match stats',
  title,
  stats,
  className,
}) => {
  const defaultTitle = version === 'match stats' ? 'MATCH STATS' : 'SEASON STATS';
  const isSeasonStats = version === 'season stats';

  return (
    <div
      className={twMerge(
        'flex flex-col items-start px-[--padding-md] py-[--padding-zero] w-full',
        // Season stats has a larger gap between title and chart grid.
        isSeasonStats ? 'gap-[--padding-md]' : 'gap-[--padding-xs]',
        // Mobile: 16px side padding. Desktop: 80px side padding (Figma large variant).
        'md:px-[--padding-7xl]',
        className
      )}
    >
      {/* Mobile: subheadline050 (20px bold). Desktop: 24px bold (subheadline060). */}
      <Typography
        as="p"
        variant="subheadline050"
        className="text-[--stats-text] text-center w-full md:text-[20px] md:leading-32"
      >
        {title ?? defaultTitle}
      </Typography>

      {isSeasonStats ? (
        /* Season stats: wrapping grid of donut pie charts */
        <div className="flex flex-wrap gap-[--padding-md] items-start justify-center w-full">
          {stats.map((stat, i) => (
            <PieGraph
              key={i}
              label={stat.label}
              homeValue={stat.homeValue}
              awayValue={stat.awayValue}
            />
          ))}
        </div>
      ) : (
        /* Match stats: vertical list of proportional bar graphs */
        <div className="flex flex-col gap-[--padding-zero] items-start w-full">
          {stats.map((stat, i) => (
            <BarGraph
              key={i}
              label={stat.label}
              homeValue={stat.homeValue}
              awayValue={stat.awayValue}
            />
          ))}
        </div>
      )}
    </div>
  );
};
